// filepath: client/src/components/SEO/HowToSEO.tsx
import React from 'react';

interface HowToStep {
  name: string;
  text: string;
  image?: string;
  url?: string;
}

interface HowToSEOProps {
  name: string;
  description: string;
  steps: HowToStep[];
  totalTime?: string;
  estimatedCost?: string;
  supply?: string[];
  tool?: string[];
}

// Pre-defined HowTo guides for FlahaFast
export const HOWTO_GUIDES = {
  nutrientCalculation: {
    name: "How to Calculate Hydroponic Nutrient Solutions with FlahaFast",
    description: "Complete step-by-step guide to calculate precise hydroponic nutrient solutions using FlahaFast professional calculator",
    totalTime: "PT15M",
    estimatedCost: "$0",
    supply: [
      "Water quality test results",
      "Target crop information",
      "Nutrient salt inventory",
      "Stock solution containers"
    ],
    tool: [
      "FlahaFast Calculator",
      "pH meter",
      "EC/TDS meter",
      "Measuring equipment"
    ],
    steps: [
      {
        name: "Analyze Water Quality",
        text: "Input your water source parameters including pH, EC, and ion concentrations into the FlahaFast water quality analyzer. This ensures accurate base calculations.",
        url: "https://flahafast.flaha.org/water-profile"
      },
      {
        name: "Select Crop Formulation",
        text: "Choose the appropriate crop formulation from our professional database based on your target crop type and growth stage.",
        url: "https://flahafast.flaha.org/formulation"
      },
      {
        name: "Configure Nutrient Salts",
        text: "Select compatible nutrient salts from our comprehensive library, ensuring proper solubility and chemical compatibility.",
        url: "https://flahafast.flaha.org/salt-library"
      },
      {
        name: "Setup Stock Solutions",
        text: "Configure your stock solution preparation with appropriate concentration factors and dilution ratios for your system.",
        url: "https://flahafast.flaha.org/stock-solutions"
      },
      {
        name: "Execute Calculation",
        text: "Run the unified calculation engine to generate precise nutrient recipes with detailed preparation instructions and quality checks.",
        url: "https://flahafast.flaha.org/calculation"
      }
    ]
  },

  waterQualityAnalysis: {
    name: "How to Analyze Water Quality for Hydroponics",
    description: "Professional guide to analyzing and adjusting water quality parameters for optimal hydroponic nutrient solutions",
    totalTime: "PT10M",
    estimatedCost: "$0",
    supply: [
      "Water sample",
      "Test results or meters"
    ],
    tool: [
      "pH meter",
      "EC/TDS meter",
      "Ion test kit or lab results"
    ],
    steps: [
      {
        name: "Test pH Level",
        text: "Measure the pH of your water source. Optimal range for most hydroponic crops is 5.5-6.5.",
        url: "https://flahafast.flaha.org/water-profile"
      },
      {
        name: "Measure Electrical Conductivity",
        text: "Test EC/TDS to determine dissolved mineral content. This affects nutrient availability and uptake.",
        url: "https://flahafast.flaha.org/water-profile"
      },
      {
        name: "Analyze Ion Concentrations",
        text: "Input individual ion concentrations (Ca²⁺, Mg²⁺, K⁺, Na⁺, SO₄²⁻, Cl⁻) for precise calculations.",
        url: "https://flahafast.flaha.org/water-profile"
      },
      {
        name: "Review Recommendations",
        text: "FlahaFast will analyze your water quality and provide recommendations for adjustments or treatments needed.",
        url: "https://flahafast.flaha.org/water-profile"
      }
    ]
  },

  stockSolutionPrep: {
    name: "How to Prepare Stock Solutions for Hydroponics",
    description: "Professional guide to preparing concentrated stock solutions for hydroponic nutrient systems",
    totalTime: "PT30M",
    estimatedCost: "$50-200",
    supply: [
      "Nutrient salts",
      "Distilled water",
      "Storage containers",
      "Labels"
    ],
    tool: [
      "Digital scale",
      "Mixing containers",
      "pH meter",
      "EC meter"
    ],
    steps: [
      {
        name: "Calculate Concentrations",
        text: "Use FlahaFast to calculate the exact amounts of each nutrient salt needed for your desired stock solution concentration.",
        url: "https://flahafast.flaha.org/stock-solutions"
      },
      {
        name: "Prepare Part A Solution",
        text: "Dissolve calcium-containing salts and micronutrients in distilled water according to calculated ratios.",
        url: "https://flahafast.flaha.org/stock-solutions"
      },
      {
        name: "Prepare Part B Solution",
        text: "Dissolve phosphate and sulfate-containing salts in separate container to prevent precipitation.",
        url: "https://flahafast.flaha.org/stock-solutions"
      },
      {
        name: "Test and Adjust",
        text: "Verify pH and EC of each stock solution, adjust if necessary, and label containers with preparation date and dilution ratios.",
        url: "https://flahafast.flaha.org/stock-solutions"
      }
    ]
  }
};

const HowToSEO: React.FC<HowToSEOProps> = ({
  name,
  description,
  steps,
  totalTime,
  estimatedCost,
  supply = [],
  tool = []
}) => {
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": name,
    "description": description,
    "image": "https://flahafast.flaha.org/assets/images/flahafast-screenshot.png",
    "totalTime": totalTime,
    "estimatedCost": {
      "@type": "MonetaryAmount",
      "currency": "USD",
      "value": estimatedCost
    },
    "supply": supply.map(item => ({
      "@type": "HowToSupply",
      "name": item
    })),
    "tool": tool.map(item => ({
      "@type": "HowToTool",
      "name": item
    })),
    "step": steps.map((step, index) => ({
      "@type": "HowToStep",
      "position": index + 1,
      "name": step.name,
      "text": step.text,
      "image": step.image,
      "url": step.url
    })),
    "author": {
      "@type": "Organization",
      "name": "Flaha Agri Tech",
      "url": "https://flahafast.flaha.org"
    },
    "datePublished": "2025-01-11",
    "dateModified": "2025-01-11"
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(howToSchema, null, 0)
      }}
    />
  );
};

export default HowToSEO;
